import { useState, useRef } from "react";

import {
    isNavigationKey,
    getCellProperty,
    isAlphaNumericKey,
    keyCodes,
} from "@silevis/reactgrid";
import "./customCellStyle.css";

import { Grid } from "@mui/material";

import {
    MenuDots,
    TrashBinMinimalistic,
    Copy,
    AltArrowRight,
    TagHorizontal,
} from "@solar-icons/react/ssr";

import HighlightCell from "@/sheet/HighlightCell";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import {
    LINE_ITEM_ROW_ID_PREFIX,
    LINE_GROUP_ROW_ID_PREFIX,
    ADDON_ROW_ID_PREFIX,
} from "@/sheet/constants";
import {
    getAddon,
    getLineGroup,
    getLineItem,
    getShowRunning,
} from "@/sheet/bidStore";
import LineGroupMenu from "@/sheet/LineGroupMenu";
import ContextMenuCellWrapper from "./ContextMenuCellWrapper";

export default class CustomChevronCellTemplate {
    constructor(
        expandGroup,
        openCopyConfirmation,
        openDeleteConfirmation,
        openAddonForm,
        lastExpandedAt,
    ) {
        this.expandGroup = expandGroup;
        this.openCopyConfirmation = openCopyConfirmation;
        this.openDeleteConfirmation = openDeleteConfirmation;
        this.openAddonForm = openAddonForm;
        this.lastExpandedAt = lastExpandedAt;
    }

    getStyle() {
        return {
            overflow: "visible",
        };
    }

    getCompatibleCell(uncertainCell) {
        const text = getCellProperty(uncertainCell, "text", "string");
        const value = parseFloat(text);
        return { ...uncertainCell, text, value };
    }

    handleKeyDown(cell, keyCode, ctrl, shift, alt) {
        if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
            const char = String.fromCharCode(keyCode);
            const updatedCell = this.update(cell, {
                ...cell,
                text: char,
                value: parseFloat(char),
            });
            return { cell: updatedCell, enableEditMode: true };
        }
        return {
            cell,
            enableEditMode:
                keyCode == keyCodes.POINTER || keyCode == keyCodes.ENTER,
        };
    }

    update(cell, cellToMerge) {
        return this.getCompatibleCell({ ...cell, text: cellToMerge.text });
    }

    render(cell, isInEditMode, onCellChanged = (cell, commit) => {}) {
        return (
            <CustomCheveronCell
                cell={cell}
                isInEditMode={isInEditMode}
                expandGroup={this.expandGroup}
                onCellChanged={onCellChanged}
                getCompatibleCell={this.getCompatibleCell}
                copyRow={this.openCopyConfirmation}
                deleteRow={this.openDeleteConfirmation}
                openAddonForm={this.openAddonForm}
                lastExpandedAt={this.lastExpandedAt}
            />
        );
    }
}

const CustomCheveronCell = ({
    cell,
    isInEditMode,
    expandGroup,
    onCellChanged,
    getCompatibleCell,
    copyRow,
    deleteRow,
    openAddonForm,
    lastExpandedAt,
}) => {
    const [hover, setHover] = useState();
    const handleMouseEnter = () => {
        setHover(true);
    };
    const handleMouseLeave = () => {
        setHover(false);
    };
    return (
        <ContextMenuCellWrapper cell={cell}>
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {isInEditMode ? (
                    <CustomChevronCellEditMode
                        cell={cell}
                        onCellChanged={onCellChanged}
                        getCompatibleCell={getCompatibleCell}
                    />
                ) : (
                    <CustomChevronCellReadMode
                        cell={cell}
                        expandGroup={expandGroup}
                        copyRow={copyRow}
                        deleteRow={deleteRow}
                        openAddonForm={openAddonForm}
                    />
                )}
                <HighlightCell hover={hover} cell={cell} />
            </div>
        </ContextMenuCellWrapper>
    );
};

const CustomChevronCellReadMode = ({
    cell,
    expandGroup,
    copyRow,
    deleteRow,
    openAddonForm,
}) => {
    const [pointerOver, setPointerOver] = useState(false);
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const [copyPopupOpen, setCopyPopupOpen] = useState(false);
    const [lineGroupMenuAnchor, setLineGroupMenuAnchor] = useState(null);

    const menuRef = useRef();
    const [prefix, id] = cell.rowId.split("-");

    const isRunning = getShowRunning();
    const lineGroup =
        prefix == LINE_GROUP_ROW_ID_PREFIX ? getLineGroup(id, isRunning) : null;
    const lineItem =
        prefix == LINE_ITEM_ROW_ID_PREFIX ? getLineItem(id, isRunning) : null;
    const addon = prefix == ADDON_ROW_ID_PREFIX ? getAddon(id) : null;

    const canEdit =
        !isRunning ||
        (isRunning &&
            ((lineItem && lineItem.line_item_id == null) ||
                (lineGroup && lineGroup.line_group_id == null)));

    const handleOpenMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setLineGroupMenuAnchor(menuRef.current);
    };

    const handleCopy = (e) => {
        copyRow(cell.rowId);
    };

    const handleDeletePopupOpen = () => {
        setDeletePopupOpen(true);
    };

    const handleCopyPopupOpen = () => {
        setCopyPopupOpen(true);
    };

    const handleDelete = (e) => {
        deleteRow(cell.rowId);
    };

    const handleOpenAddonPopup = (e) => {
        const addonId = cell.rowId.split("-")[1];
        openAddonForm(addonId);
    };

    const handlePointerOver = () => {
        if (!cell.nonEditable) {
            setPointerOver(true);
        }
    };

    const handlePointerLeave = () => {
        if (!cell.nonEditable) {
            setPointerOver(false);
        }
    };

    return (
        <div
            style={{ width: "100%" }}
            onPointerEnter={handlePointerOver}
            onPointerLeave={handlePointerLeave}
        >
            <Grid
                container
                justifyContent={"space-between"}
                alignItems="center"
                wrap="nowrap"
                width={290}
            >
                <Grid item>
                    <Grid container alignItems={"center"} wrap="nowrap">
                        <Grid item width={32} marginLeft={cell.indent * 3}>
                            {pointerOver && canEdit && (
                                <Grid container wrap="nowrap" height={14}>
                                    <Grid item>
                                        <TrashBinMinimalistic
                                            size={14}
                                            onClick={handleDeletePopupOpen}
                                            style={{
                                                cursor: "pointer",
                                                color: "grey",
                                            }}
                                        />
                                    </Grid>
                                    <Grid item paddingRight={1}>
                                        <Copy
                                            size={14}
                                            onClick={
                                                lineGroup
                                                    ? handleCopyPopupOpen
                                                    : handleCopy
                                            }
                                            style={{
                                                cursor: "pointer",
                                                color: "grey",
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item maxWidth={165} overflow={"auto"}>
                            <span style={{ paddingRight: 10 }}>
                                {cell.text}
                            </span>
                        </Grid>
                        {!cell.nonEditable && cell.hasChildren && (
                            <Grid item ref={menuRef} height={"12px"}>
                                <MenuDots
                                    size={14}
                                    onClick={handleOpenMenu}
                                    style={{ cursor: "pointer" }}
                                />
                            </Grid>
                        )}
                        {cell.rowId.startsWith("addon") && (
                            <Grid item>
                                <TagHorizontal
                                    size={14}
                                    onClick={handleOpenAddonPopup}
                                    style={{ cursor: "pointer" }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item>
                    <Chevron cell={cell} expandGroup={expandGroup} />
                </Grid>
            </Grid>

            {lineItem && (
                <ConfirmationDialog
                    open={deletePopupOpen}
                    closeDialog={() => setDeletePopupOpen(false)}
                    title={`Delete "${lineItem.name}"?`}
                    callback={handleDelete}
                >
                    <p>{"Are you sure you want to delete this line item?"}</p>
                </ConfirmationDialog>
            )}

            {addon && (
                <ConfirmationDialog
                    open={deletePopupOpen}
                    closeDialog={() => setDeletePopupOpen(false)}
                    title={`Delete "${addon.name}"?`}
                    callback={handleDelete}
                >
                    <p>{"Are you sure you want to delete this add-on?"}</p>
                </ConfirmationDialog>
            )}

            {lineGroup && (
                <ConfirmationDialog
                    open={deletePopupOpen}
                    closeDialog={() => setDeletePopupOpen(false)}
                    title={`Delete "${lineGroup.name}"?`}
                    callback={handleDelete}
                >
                    <p>
                        {
                            "Deleting a group will remove all line items contained inside. This is non-reversable."
                        }
                    </p>
                </ConfirmationDialog>
            )}

            {lineGroup && (
                <ConfirmationDialog
                    open={copyPopupOpen}
                    closeDialog={() => setCopyPopupOpen(false)}
                    title={`Copy "${lineGroup.name}"?`}
                    callback={handleCopy}
                >
                    <p>{"This will copy all its line items and addons"}</p>
                </ConfirmationDialog>
            )}

            <div
                onPointerDown={(e) => {
                    e.stopPropagation();
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                }}
            >
                <LineGroupMenu
                    openCopy={handleCopyPopupOpen}
                    openDelete={handleDeletePopupOpen}
                    anchorEl={lineGroupMenuAnchor}
                    lineGroupData={lineGroup}
                    close={() => {
                        setLineGroupMenuAnchor(null);
                    }}
                />
            </div>
        </div>
    );
};

const CustomChevronCellEditMode = ({
    cell,
    onCellChanged,
    getCompatibleCell,
}) => {
    return (
        <input
            ref={(input) => {
                input && input.focus();
            }}
            defaultValue={cell.text}
            onChange={(e) =>
                onCellChanged(
                    getCompatibleCell({ ...cell, text: e.currentTarget.value }),
                    false,
                )
            }
            onCopy={(e) => e.stopPropagation()}
            onCut={(e) => e.stopPropagation()}
            onPaste={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode))
                    e.stopPropagation();
            }}
            onPointerEnter={(e) => e.stopPropagation()}
            onBlur={(e) => {
                onCellChanged(
                    getCompatibleCell({
                        ...cell,
                        text: e.currentTarget.value,
                        value: parseFloat(e.currentTarget.value),
                    }),
                    true,
                );
            }}
            style={{
                outline: "none",
                border: "none",
            }}
        />
    );
};

const Chevron = ({ cell, expandGroup }) => {
    const handleExpand = () => {
        expandGroup(cell.rowId, !cell.isExpanded);
    };

    const baseStyle = {
        cursor: "pointer",
        padding: "12px 8px 8px 10px",
    };
    const expandedStyle = {
        transform: "rotate(90deg)",
        padding: "12px 6px 8px 8px",
    };

    return cell.hasChildren ? (
        <AltArrowRight
            size={14}
            onClick={handleExpand}
            style={{
                ...baseStyle,
                ...(cell.isExpanded && expandedStyle),
            }}
        />
    ) : null;
};
